import * as React from 'react';
import { UIHeading1, UIText1, UIText3, Button, MaskedInput, InputAdornment, UIHeading2, Checkbox } from '@cian/ui-kit';
import clsx from 'classnames';

import { ECPDDraftStatus } from 'shared/types/redux/cpd';
import { isValidPhoneNumber } from 'shared/utils/cpd';

import { ICPDPrelandingContent } from '../../types';

import styles from './PrelandingModalContent.css';

interface IPrelandingModalContentProps {
  onModalOpen: VoidFunction;
  onCreateDraftCpd: VoidFunction;
  onCPDLandingLinkClick: VoidFunction;
  cpdDraftStatus: ECPDDraftStatus;
  prelandingContent: ICPDPrelandingContent;
  phoneValue?: string | null;
  onInputChange: (val: string) => void;
  cpdLandingLink: string | null;
  isMobile: boolean;
  handleChangeMarketingAgreement: () => void;
  isMarketingAgreement: boolean;
}

export const PrelandingModalContent: React.FC<IPrelandingModalContentProps> = props => {
  const {
    onModalOpen,
    onCreateDraftCpd,
    onCPDLandingLinkClick,
    cpdDraftStatus,
    prelandingContent,
    phoneValue,
    onInputChange,
    cpdLandingLink,
    isMobile,
    isMarketingAgreement,
    handleChangeMarketingAgreement,
  } = props;

  const phoneInputRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    onModalOpen();
  }, [onModalOpen]);

  React.useLayoutEffect(() => {
    if (!phoneValue) {
      phoneInputRef.current?.querySelector('input')?.focus();
    }
  }, [phoneValue]);

  const isPhoneValid = isValidPhoneNumber(phoneValue);

  const {
    mainDesktopImgUrl,
    title,
    points,
    primaryBtnText,
    secondaryBtn,
    securyPersonalUrl,
    securyConfidentialityUrl,
    marketingAgreementUrl,
  } = prelandingContent;

  const HeadingTypography = isMobile ? UIHeading2 : UIHeading1;

  const legal = (() => {
    if (isMobile) {
      return (
        <UIText3 color="gray60_100">
          Нажимая «Позвоните мне»,
          <br /> я&nbsp;даю{' '}
          <a href={securyPersonalUrl} target="_blank" className={styles['link']} rel="noreferrer">
            Согласие&nbsp;на&nbsp;обработку&nbsp;персональных&nbsp;данных
          </a>{' '}
          в&nbsp;соответствии с&nbsp;
          <a href={securyConfidentialityUrl} target="_blank" className={styles['link']} rel="noreferrer">
            Политикой&nbsp;конфиденциальности
          </a>
        </UIText3>
      );
    }

    return (
      <UIText3 color="text-secondary-default">
        Нажимая «Позвоните мне», я&nbsp;даю{' '}
        <a href={securyPersonalUrl} target="_blank" className={styles['link']} rel="noreferrer">
          Согласие на&nbsp;обработку персональных данных
        </a>{' '}
        в&nbsp;соответствии с&nbsp;
        <a href={securyConfidentialityUrl} target="_blank" className={styles['link']} rel="noreferrer">
          Политикой конфиденциальности
        </a>
      </UIText3>
    );
  })();

  const marketingAgreement = marketingAgreementUrl && (
    <div className={styles['marketing']} data-name="AgreementMarketing">
      <Checkbox checked={isMarketingAgreement} onChange={handleChangeMarketingAgreement} />
      <UIText3 color="text-secondary-default">
        Соглашаюсь на{' '}
        <a href={marketingAgreementUrl} className={styles['link']} target="_blank" rel="noopener noreferrer">
          рассылки и звонки
        </a>
      </UIText3>
    </div>
  );

  return (
    <div className={clsx(styles['wrapper'], isMobile && styles['mobile'])} data-testid="CPDPrelandingModalMain">
      <img src={mainDesktopImgUrl} className={styles['img']} />
      <div className={styles['content']}>
        <div className={styles['title']}>
          <HeadingTypography>{title}</HeadingTypography>
        </div>
        <div className={styles['list']}>
          {points.map((point, i) => {
            return (
              <div key={i} className={styles['row']}>
                <div className={styles['icon']}>
                  <img src={point.iconUrl} />
                </div>
                <UIText1>{point.text}</UIText1>
              </div>
            );
          })}
        </div>

        {cpdLandingLink && secondaryBtn?.text && (
          <div className={styles['secondary']}>
            <UIText1 color="text-main-default">
              <a
                className={styles['link']}
                rel="noreferrer"
                href={cpdLandingLink}
                target="_blank"
                onClick={onCPDLandingLinkClick}
              >
                {secondaryBtn.text}
              </a>
            </UIText1>
          </div>
        )}

        <div className={styles['controls']}>
          <div className={styles['phone']}>
            <MaskedInput
              ref={phoneInputRef}
              size="M"
              value={phoneValue?.replace('7', '')}
              onChange={phone => onInputChange(`7${phone}`)}
              mask="(000) 000 00-00"
              leftAdornment={<InputAdornment>+7</InputAdornment>}
              width="100%"
              data-testid="CPDPrelandingModalPhoneInput"
              disabled={cpdDraftStatus === ECPDDraftStatus.Loading}
            />
          </div>

          <Button
            onClick={onCreateDraftCpd}
            theme="fill_primary"
            fullWidth={isMobile}
            data-testid="CPDPrelandingModalSubmitCPDRequest"
            disabled={!isPhoneValid || cpdDraftStatus === ECPDDraftStatus.Loading}
          >
            {primaryBtnText}
          </Button>
        </div>

        <div className={styles['legal']} data-testid="legalNotes">
          {marketingAgreement}
          {legal}
        </div>
      </div>
    </div>
  );
};
